<script setup>
import StatusChip from '@/components/shared/StatusChip.vue'
import { formatCurrency, formatDate } from '@core/utils/formatters'
import {
  billingCadenceLabel,
  useSubscriptionsStore,
} from '@/stores/subscriptions'
import { useSessionPackagesStore } from '@/stores/sessionPackages'
import {
  paymentMethodLabel,
  usePaymentsStore,
} from '@/stores/payments'

definePage({
  meta: {
    layout: 'client',
    clientArea: true,
  },
})

const store = useSubscriptionsStore()
const packagesStore = useSessionPackagesStore()
const paymentsStore = usePaymentsStore()
const { t } = useI18n()

const formError = ref('')
const formSuccess = ref('')
const signalDialog = ref(false)
const signalInvoice = ref(null)
const signalAmountEuros = ref('')
const signalPaymentMethod = ref('tikkie')
const route = useRoute()
const router = useRouter()
const payingInvoiceId = ref(null)

const subscription = computed(() => store.openSubscription || store.subscriptions[0] || null)

const invoiceHeaders = computed(() => [
  { title: t('subscriptions.invoiceColumns.description'), key: 'description' },
  { title: t('subscriptions.invoiceColumns.amount'), key: 'amount' },
  { title: t('subscriptions.invoiceColumns.dueOn'), key: 'due_on' },
  { title: t('subscriptions.invoiceColumns.status'), key: 'lifecycle_state' },
  { title: t('subscriptions.invoiceColumns.actions'), key: 'actions', sortable: false },
])

const paymentHeaders = computed(() => [
  { title: t('payments.columns.date'), key: 'date' },
  { title: t('payments.columns.amount'), key: 'amount' },
  { title: t('payments.columns.method'), key: 'payment_method' },
  { title: t('payments.columns.status'), key: 'lifecycle_state' },
  { title: t('payments.columns.invoice'), key: 'invoice_description' },
])

const paymentMethodItems = computed(() => [
  { title: t('subscriptions.paymentMethods.tikkie'), value: 'tikkie' },
  { title: t('subscriptions.paymentMethods.cash'), value: 'cash' },
  { title: t('subscriptions.paymentMethods.bank_transfer'), value: 'bank_transfer' },
  { title: t('subscriptions.paymentMethods.other'), value: 'other' },
])

const canSignal = state => ['sent', 'overdue'].includes(state)

const canPayWithMollie = item => {
  return canSignal(item.lifecycle_state)
    && (item.mollie_checkout_url || item.mollie_link_available)
}

const payWithMollie = async invoice => {
  formError.value = ''
  formSuccess.value = ''
  payingInvoiceId.value = invoice.id
  try {
    if (invoice.mollie_checkout_url) {
      window.location.href = invoice.mollie_checkout_url

      return
    }

    const data = await store.payInvoiceWithMollie(invoice.id)
    if (data?.checkout_url) {
      window.location.href = data.checkout_url

      return
    }

    formError.value = t('subscriptions.molliePayError')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || store.error || t('subscriptions.molliePayError')
  }
  finally {
    payingInvoiceId.value = null
  }
}

const applyMollieReturnMessage = async () => {
  if (route.query.mollie !== 'return')
    return

  const invoiceId = Number(route.query.invoice_id)

  await store.fetchClientOwn().catch(() => null)

  if (invoiceId) {
    const invoice = store.invoices.find(i => i.id === invoiceId)
    if (invoice?.lifecycle_state === 'paid')
      formSuccess.value = t('subscriptions.mollieReturnPaid')
    else if (invoice?.mollie_payment_status === 'failed')
      formError.value = t('subscriptions.mollieReturnCancelled')
    else
      formSuccess.value = t('subscriptions.mollieReturnPending')
  }
  else {
    formSuccess.value = t('subscriptions.mollieReturnPending')
  }

  const nextQuery = { ...route.query }

  delete nextQuery.mollie
  delete nextQuery.invoice_id
  router.replace({ query: nextQuery })
}

const invoiceAmountLabel = item => {
  const total = formatCurrency(item.amount_cents, item.currency)
  const remaining = item.remaining_balance_cents
  if (
    remaining != null
    && remaining > 0
    && remaining < item.amount_cents
    && canSignal(item.lifecycle_state)
  ) {
    return `${total} · ${t('payments.remaining', { amount: formatCurrency(remaining, item.currency) })}`
  }

  return total
}

const paymentDate = payment => {
  if (payment.paid_at)
    return formatDate(payment.paid_at)

  return formatDate(payment.created_at)
}

const openSignalDialog = invoice => {
  signalInvoice.value = invoice

  const remainingCents = invoice.remaining_balance_cents ?? invoice.amount_cents

  signalAmountEuros.value = (remainingCents / 100).toFixed(2).replace('.', ',')
  signalPaymentMethod.value = 'tikkie'
  signalDialog.value = true
}

const submitSignal = async () => {
  if (!signalInvoice.value)
    return
  formError.value = ''
  formSuccess.value = ''
  try {
    const normalized = String(signalAmountEuros.value).replace(',', '.')
    const euros = Number.parseFloat(normalized)
    const amountCents = Number.isFinite(euros) ? Math.round(euros * 100) : null

    const data = await paymentsStore.signalPayment({
      invoiceId: signalInvoice.value.id,
      amountCents,
      paymentMethod: signalPaymentMethod.value,
    })

    if (data?.invoice) {
      store.invoices = store.invoices.map(i =>
        i.id === data.invoice.id ? data.invoice : i,
      )
    }
    signalDialog.value = false
    formSuccess.value = t('payments.signalSuccess')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || paymentsStore.error
  }
}

onMounted(async () => {
  await Promise.all([
    store.fetchClientOwn().catch(() => null),
    packagesStore.fetchClientOwn().catch(() => null),
    paymentsStore.fetchClientOwn().catch(() => null),
  ])
  await applyMollieReturnMessage()
})
</script>

<template>
  <div class="pa-6">
    <div class="mb-6">
      <h1 class="text-h4 mb-1">
        {{ $t('subscriptions.clientTitle') }}
      </h1>
      <p class="text-body-2 text-medium-emphasis mb-0">
        {{ $t('subscriptions.clientSubtitle') }}
      </p>
    </div>

    <VAlert
      v-if="formError"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ formError }}
    </VAlert>
    <VAlert
      v-if="formSuccess"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ formSuccess }}
    </VAlert>

    <VCard
      flat
      border
      class="mb-4"
    >
      <VCardItem>
        <VCardTitle>{{ $t('sessionPackages.clientTitle') }}</VCardTitle>
        <VCardSubtitle>{{ $t('sessionPackages.clientSubtitle') }}</VCardSubtitle>
      </VCardItem>
      <VCardText>
        <div
          v-if="packagesStore.loading"
          role="status"
          class="text-medium-emphasis"
        >
          {{ $t('sessionPackages.loading') }}
        </div>
        <VAlert
          v-else-if="packagesStore.error"
          type="error"
          variant="tonal"
          role="alert"
        >
          {{ packagesStore.error }}
        </VAlert>
        <VAlert
          v-else-if="packagesStore.isEmpty"
          type="info"
          variant="tonal"
          role="status"
        >
          {{ $t('sessionPackages.clientEmpty') }}
        </VAlert>
        <VList
          v-else
          lines="two"
          class="bg-transparent"
        >
          <VListItem
            v-for="pkg in packagesStore.packages"
            :key="pkg.id"
          >
            <VListItemTitle>
              {{ pkg.plan_name }}
              ·
              {{ $t('sessionPackages.remaining', { count: pkg.sessions_remaining }) }}
            </VListItemTitle>
            <VListItemSubtitle>
              {{ $t('sessionPackages.expiresOn', { date: formatDate(pkg.expires_on) }) }}
            </VListItemSubtitle>
            <template #append>
              <StatusChip
                domain="session_package"
                :state="pkg.lifecycle_state"
              />
            </template>
          </VListItem>
        </VList>
      </VCardText>
    </VCard>

    <div
      v-if="store.loading"
      role="status"
      class="text-medium-emphasis"
    >
      {{ $t('subscriptions.loading') }}
    </div>

    <VAlert
      v-else-if="store.error"
      type="error"
      variant="tonal"
      role="alert"
    >
      {{ store.error }}
    </VAlert>

    <VAlert
      v-else-if="!subscription && !store.invoices.length"
      type="info"
      variant="tonal"
      role="status"
    >
      {{ $t('subscriptions.clientEmpty') }}
    </VAlert>

    <template v-else>
      <VCard
        v-if="subscription"
        flat
        border
        class="mb-4"
      >
        <VCardText>
          <div class="d-flex flex-wrap align-center gap-3 mb-2">
            <StatusChip
              domain="subscription"
              :state="subscription.lifecycle_state"
            />
            <span class="text-h6">
              {{ subscription.plan_name }}
            </span>
          </div>
          <p class="text-body-1 mb-1">
            {{ formatCurrency(subscription.price_cents, subscription.currency) }}
            · {{ billingCadenceLabel(subscription.billing_cadence) }}
          </p>
          <p
            v-if="subscription.next_billing_on && ['active', 'past_due', 'paused'].includes(subscription.lifecycle_state)"
            class="text-body-2 text-medium-emphasis mb-0"
          >
            {{ $t('subscriptions.nextBilling', { date: subscription.next_billing_on }) }}
          </p>
          <VAlert
            v-if="subscription.lifecycle_state === 'past_due'"
            type="warning"
            variant="tonal"
            class="mt-4"
            role="status"
          >
            {{ $t('subscriptions.clientPastDueNotice') }}
          </VAlert>
        </VCardText>
      </VCard>

      <VCard
        flat
        border
        class="mb-4"
      >
        <VCardItem>
          <VCardTitle>{{ $t('subscriptions.invoicesTitle') }}</VCardTitle>
        </VCardItem>
        <VCardText>
          <VAlert
            v-if="!store.invoices.length"
            type="info"
            variant="tonal"
            role="status"
          >
            {{ $t('subscriptions.invoicesEmpty') }}
          </VAlert>
          <VDataTable
            v-else
            :headers="invoiceHeaders"
            :items="store.sortedInvoices"
            :items-per-page="10"
            density="comfortable"
            class="elevation-0"
          >
            <template #item.amount="{ item }">
              {{ invoiceAmountLabel(item) }}
            </template>
            <template #item.due_on="{ item }">
              {{ formatDate(item.due_on) }}
            </template>
            <template #item.lifecycle_state="{ item }">
              <StatusChip
                domain="invoice"
                :state="item.lifecycle_state"
              />
            </template>
            <template #item.actions="{ item }">
              <div class="d-flex flex-wrap ga-1 justify-end">
                <VBtn
                  v-if="canPayWithMollie(item)"
                  size="small"
                  color="primary"
                  :loading="payingInvoiceId === item.id || store.saving"
                  :aria-label="$t('subscriptions.payWithMollie')"
                  @click="payWithMollie(item)"
                >
                  {{ $t('subscriptions.payWithMollie') }}
                </VBtn>
                <VBtn
                  v-if="canSignal(item.lifecycle_state)"
                  size="small"
                  color="primary"
                  variant="tonal"
                  :loading="paymentsStore.saving"
                  :aria-label="$t('payments.signalPaid')"
                  @click="openSignalDialog(item)"
                >
                  {{ $t('payments.signalPaid') }}
                </VBtn>
              </div>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>

      <VCard
        flat
        border
      >
        <VCardItem>
          <VCardTitle>{{ $t('payments.clientTitle') }}</VCardTitle>
          <VCardSubtitle>{{ $t('payments.clientSubtitle') }}</VCardSubtitle>
        </VCardItem>
        <VCardText>
          <div
            v-if="paymentsStore.loading && paymentsStore.isEmpty"
            role="status"
            class="text-medium-emphasis"
          >
            {{ $t('payments.loading') }}
          </div>
          <VAlert
            v-else-if="paymentsStore.error && paymentsStore.isEmpty"
            type="error"
            variant="tonal"
            role="alert"
          >
            {{ paymentsStore.error }}
          </VAlert>
          <VAlert
            v-else-if="paymentsStore.isEmpty"
            type="info"
            variant="tonal"
            role="status"
          >
            {{ $t('payments.empty') }}
          </VAlert>
          <VDataTable
            v-else
            :headers="paymentHeaders"
            :items="paymentsStore.payments"
            :items-per-page="10"
            density="comfortable"
            class="elevation-0"
          >
            <template #item.date="{ item }">
              {{ paymentDate(item) }}
            </template>
            <template #item.amount="{ item }">
              {{ formatCurrency(item.amount_cents, item.currency) }}
            </template>
            <template #item.payment_method="{ item }">
              {{ paymentMethodLabel(item.payment_method) }}
            </template>
            <template #item.lifecycle_state="{ item }">
              <StatusChip
                domain="payment"
                :state="item.lifecycle_state"
              />
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </template>

    <VDialog
      v-model="signalDialog"
      max-width="480"
    >
      <VCard :title="$t('payments.signalDialogTitle')">
        <VCardText>
          <p class="text-body-2 mb-4">
            {{ $t('payments.signalDialogBody') }}
          </p>
          <VTextField
            v-model="signalAmountEuros"
            :label="$t('payments.amount')"
            :hint="$t('payments.amountHint')"
            persistent-hint
            class="mb-4"
            inputmode="decimal"
          />
          <VSelect
            v-model="signalPaymentMethod"
            :items="paymentMethodItems"
            :label="$t('subscriptions.paymentMethod')"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="signalDialog = false"
          >
            {{ $t('payments.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="paymentsStore.saving"
            @click="submitSignal"
          >
            {{ $t('payments.signalPaid') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
